import {Checkbox} from 'antd';
import {CheckboxChangeEvent} from 'antd/es/checkbox';
import React, {useEffect, useState, FC} from 'react';

import {IFieldsState} from 'components/table/table-types';
import {IFieldMeta} from 'entities/metadata';
import {TEntity, TEntityValue} from 'modules/data';
import {useTableCellWatcher} from 'shared/hooks/use-table-cell-watcher';
import {TStateSetter} from 'shared/types/generics';
import {normalizeBoolValue, normalizeBoolValueString} from 'utils/common';

interface IBoolCellProps {
    value: TEntityValue;
    record: TEntity;
    field?: IFieldMeta;
    handleChangeIndexRow?: (value: TEntity, key: string, dependentsFieldKey?: string) => void;
    setFieldsState?: TStateSetter<IFieldsState>;
}

export const BoolCell: FC<IBoolCellProps> = ({
    field,
    value,
    record,
    handleChangeIndexRow,
    setFieldsState,
}: IBoolCellProps) => {
    const [boolValue, setBoolValue] = useState(normalizeBoolValue(value));
    const {isDisabled: isWatcherDisabled} = useTableCellWatcher({
        fieldMeta: field || { key: '', label: '' } as IFieldMeta,
        record
    });

    const {booleanValues = []} = field || {};
    const normalizedValue = normalizeBoolValue(value);

    const index = +normalizedValue;

    const isComponentDisabled = field?.isDisabled || isWatcherDisabled;

    useEffect(() => {
        if (setFieldsState && field?.key) {
            setFieldsState((fieldsState: IFieldsState) => {
                const updatedFieldsState = { ...fieldsState };

                if (!updatedFieldsState[record.id as number]) {
                    updatedFieldsState[record.id as number] = {};
                }

                if (!updatedFieldsState[record.id as number][field.key as string]) {
                    updatedFieldsState[record.id as number][field.key as string] = {};
                }

                updatedFieldsState[record.id as number][field.key as string].disabled = isComponentDisabled;

                return updatedFieldsState;
            });
        }
    }, [setFieldsState, field?.key, record.id, isComponentDisabled]);

    const handleChange = (e: CheckboxChangeEvent) => {
        if (isComponentDisabled) return;

        const newValue = e.target.checked;

        const newRow = {
            ...record,
            [String(field?.key)]: normalizeBoolValueString(newValue)
        };

        setBoolValue(newValue);

        if (field?.dependentsFieldKey && handleChangeIndexRow) {
            handleChangeIndexRow(newRow, field?.key, field.dependentsFieldKey);
            return;
        }
        if (handleChangeIndexRow) {
            handleChangeIndexRow(newRow, (field?.key || ''));
        }
    };

    useEffect(() => {
        setBoolValue(normalizeBoolValue(value));
    }, [value]);

    const shouldRenderCheckbox = field?.checkForDisabledMetaFields || field?.dependentsFieldKey;

    if (shouldRenderCheckbox) {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
                checked={boolValue}
                onChange={handleChange}
                disabled={isComponentDisabled}
            />
          </div>
        );
    }

    return !booleanValues?.length ? (
        <Checkbox
            checked={normalizedValue}
            disabled
        />
    ) : (
        <span>{booleanValues[index]}</span>
    );
};
